import { Injectable, inject } from '@angular/core';
import { ApiGatewayService } from '@wam/authentication';
import { combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { WithAuthService } from '@app/shared/services/with-auth.service';
import { select } from '@ngrx/store';
import { Configuration, Job, JobSchedule, JobStatusEnum } from '@wap/batch';
import { isEmpty } from 'lodash-es';
import { documentId, Product, RosteringProvider } from '@wam/shared';
import { authenticationSelectors } from '@app/authentication/state/authentication.selectors';
import { CourseAssignmentsPayload } from '@app/organizations/batch.model';

export enum JobTypeEnum {
  REMOVE_ASSOCIATIONS = 'remove_associations',
  RESET_TO_PLACEMENT = 'resetplacement',
  RESET_ASSESSMENT_TESTS = 'reset_assessment_tests',
  UNASSIGN_COURSES = 'course_unassignment',
  COURSE_ASSIGNMENT = 'course_assignment',
  BATCH_GOALS_REMOVAL = 'batch_goals_removal',
  END_OF_YEAR_ROLLOVER = 'end_of_year_rollover',
  ARCHIVE_ASSIGNMENT = 'archive_assignment',
}

export enum ArchiveAssignmentEntityLevel {
  ALL = 'ALL',
  SCHOOL = 'SCHOOL',
  CLASS = 'CLASS',
  USER = 'USER',
}

// WACS 2.0 (Product.ayla) benchmark documents.
export const aylaCourses = [
  'assessment-start-of-year-benchmark',
  'assessment-mid-year-benchmark',
  'assessment-end-of-year-benchmark',
];

export const IN_QUEUE = 'in_queue' as JobStatusEnum;
const ACTIVE_JOB_STATUSES = new Set<string>([
  IN_QUEUE,
  JobStatusEnum.STARTED,
  JobStatusEnum.IN_PROGRESS,
]);

export enum TopEntityType {
  ORGANIZATION = 'ORGANIZATION',
  SCHOOL = 'SCHOOL',
  CLASS = 'CLASS',
  USER = 'USER',
}

export type BatchGoalsRemovalRequest = {
  product: Product;
  grade?: string;
};

const RESET_ASSESSMENT_FAILURE_KEYS = [
  'failedStudents',
  'failureStudents',
  'failedStudentIds',
  'failedStudentNames',
  'failures',
  'failed',
  'notResetStudents',
  'studentsNotReset',
  'unsuccessfulStudents',
  'students',
] as const;

export type ResetAssessmentTestFailure = string | number | ResetAssessmentTestFailureRecord;

export interface ResetAssessmentTestsJobScope {
  classIds: string[];
  studentIds: string[];
}

export interface ResetAssessmentTestFailureRecord {
  familyName?: string;
  givenName?: string;
  firstName?: string;
  lastName?: string;
  family_name?: string;
  given_name?: string;
  first_name?: string;
  last_name?: string;
  fullName?: string;
  full_name?: string;
  name?: string;
  displayName?: string;
  studentName?: string;
  student_name?: string;
  studentId?: string;
  student_id?: string;
  organizationId?: string;
  organization_id?: string;
  userId?: string;
  user_id?: string;
  uuid?: string;
  sourcedId?: string;
  id?: string | number;
  student?: ResetAssessmentTestFailure;
  user?: ResetAssessmentTestFailure;
  person?: ResetAssessmentTestFailure;
  [key: string]: unknown;
}

export const jobTypeAlreadyRunningFriendlyFormat = {
  onerostersftp: '3rd Party Sync',
  oneroster: '3rd Party Sync',
  clever: '3rd Party Sync',
  remove_association: 'Remove Association Job',
  resetplacement: 'Reset to Placement Job',
  reset_assessment_tests: 'Reset Assessment Tests Job',
  unassigncourses: 'Course Un-Assignment Job',
  course_assignment: 'Course Assignment Job',
  batch_goals_removal: 'Batch Goals Removal Job',
  archive_assignment: 'Archive Assignments Job',
};

@Injectable({
  providedIn: 'root',
})
export class BatchService extends WithAuthService {
  private apiGatewayService = inject(ApiGatewayService);

  displayRostering$ = (organizationId?: string): Observable<boolean> => {
    const provider$ = organizationId
      ? this.getRosteringProvider(organizationId)
      : this.store.pipe(select(authenticationSelectors.getRosteringProvider), take(1));
    return provider$.pipe(
      map((provider) =>
        [RosteringProvider.CLEVER, RosteringProvider.ONE_ROSTER].includes(provider),
      ),
    );
  };

  scheduleJob(
    jobType: JobTypeEnum | RosteringProvider,
    jobConfiguration: {
      hour: string;
      recurrent: boolean;
      paused: boolean;
      configuration?: unknown;
    },
    organization?: string,
  ): Observable<JobSchedule> {
    return this.withUser().pipe(
      mergeMap((user) => {
        return this.apiGatewayService.put<JobSchedule>(
          `batch/v1/sources/${organization ?? user.organization}/types/${jobType}/schedule/jobs`,
          jobConfiguration,
          'scheduledJobs',
        );
      }),
      map((job) => ({ ...job, status: IN_QUEUE })),
    );
  }

  getJob(
    jobType: JobTypeEnum | RosteringProvider,
    organizationId?: string,
  ): Observable<Job & JobSchedule> {
    return this.withUser().pipe(
      mergeMap((user) => {
        return forkJoin([
          this.apiGatewayService
            .get<unknown>(
              `batch/v1/sources/${
                organizationId ?? user.organization
              }/types/${jobType}/schedule/jobs`,
            )
            .pipe(
              map((job) => this.toBatchJob(job) ?? ({} as JobSchedule)),
              catchError((error) => {
                if (error.status === 404) {
                  return of({} as JobSchedule);
                }
                throw error;
              }),
            ),
          this.getLastJob(jobType, organizationId ?? user.organization),
        ]).pipe(
          mergeMap(([scheduledJob, latestJob]) => {
            const combinedJob = { ...scheduledJob, ...latestJob };
            if (!isEmpty(combinedJob)) {
              combinedJob.status = combinedJob.status ?? IN_QUEUE;
            }
            return of(combinedJob);
          }),
        );
      }),
    );
  }

  getLastJob(
    jobType: JobTypeEnum | RosteringProvider,
    organization: string,
    status?: string,
  ): Observable<Job> {
    const extraParam = status ? `?status=${status}` : '';
    return this.apiGatewayService
      .get<unknown>(`batch/v1/sources/${organization}/types/${jobType}/jobs${extraParam}`)
      .pipe(
        map((response) => this.toBatchJobs(response)[0] ?? null),
        catchError((error) => {
          if (error.status === 404) {
            return of(null);
          }
          throw error;
        }),
      );
  }

  getLastSuccessfulJob(jobType: JobTypeEnum, organization?: string): Observable<Job> {
    return this.withUser().pipe(
      mergeMap((user) => this.getLastJob(jobType, organization ?? user.organization, 'success')),
    );
  }

  isResetAssessmentTestsJobPending(organizationId?: string): Observable<boolean> {
    return this.getJob(JobTypeEnum.RESET_ASSESSMENT_TESTS, organizationId).pipe(
      map((job) => this.isActiveJob(job)),
      catchError(() => of(false)),
    );
  }

  getPendingResetAssessmentTestsJobScope(
    organizationId?: string,
  ): Observable<ResetAssessmentTestsJobScope | null> {
    return this.getJob(JobTypeEnum.RESET_ASSESSMENT_TESTS, organizationId).pipe(
      map((job) => (this.isActiveJob(job) ? this.toResetAssessmentTestsJobScope(job) : null)),
      catchError(() => of(null)),
    );
  }

  getFailedAssessmentResets(
    jobExecutionId: string,
    organizationId?: string,
  ): Observable<ResetAssessmentTestFailure[]> {
    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.get<Job>(
          `batch/v1/sources/${
            organizationId ?? user.organization
          }/types/${JobTypeEnum.RESET_ASSESSMENT_TESTS}/jobs/${jobExecutionId}`,
        ),
      ),
      map((job) => this.extractFailedAssessmentResets(job)),
      catchError(() => of([] as ResetAssessmentTestFailure[])),
    );
  }

  private extractFailedAssessmentResets(job: unknown): ResetAssessmentTestFailure[] {
    const attributes = this.jobAttributes(job);
    const output = this.toRecord(attributes?.['output']);
    const sources = [
      this.toRecord(output?.['summary']),
      this.toRecord(output?.['jobData']),
      this.toRecord(attributes?.['jobData']),
      output,
    ];

    for (const source of sources) {
      for (const key of RESET_ASSESSMENT_FAILURE_KEYS) {
        const failures = this.toResetAssessmentFailures(source?.[key]);
        if (failures.length > 0) {
          return failures;
        }
      }
    }

    return [];
  }

  private jobAttributes(job: unknown): Record<string, unknown> | undefined {
    const response = this.toRecord(job);
    const data = this.toRecord(response?.['data']);

    return (
      this.toRecord(data?.['attributes']) ?? this.toRecord(response?.['attributes']) ?? response
    );
  }

  private toBatchJobs(value: unknown): Job[] {
    if (Array.isArray(value)) {
      return value
        .map((job) => this.toBatchJob(job))
        .filter((job): job is Job & JobSchedule => job != null);
    }

    const data = this.toRecord(value)?.['data'];
    if (Array.isArray(data)) {
      return data
        .map((job) => this.toBatchJob(job))
        .filter((job): job is Job & JobSchedule => job != null);
    }

    const job = this.toBatchJob(value);
    return job ? [job] : [];
  }

  private toBatchJob(value: unknown): (Job & JobSchedule) | null {
    const record = this.toRecord(value);
    if (record == null) {
      return null;
    }

    const data = this.toRecord(record['data']);
    const attributes = this.toRecord(data?.['attributes']) ?? this.toRecord(record['attributes']);
    const job = attributes ? { ...attributes } : { ...record };
    const id = data?.['id'] ?? record['id'];

    if (id != null && job['id'] == null) {
      job['id'] = id;
    }

    delete job['attributes'];
    delete job['data'];

    return job as Job & JobSchedule;
  }

  private isActiveJob(job: Partial<Job> | null | undefined): boolean {
    return ACTIVE_JOB_STATUSES.has(`${job?.status ?? ''}`.toLowerCase());
  }

  private toResetAssessmentTestsJobScope(job: unknown): ResetAssessmentTestsJobScope {
    const jobRecord = this.toRecord(job);
    const startConfiguration = this.toRecord(jobRecord?.['startConfiguration']);
    const runConfiguration =
      this.toRecord(startConfiguration?.['runConfiguration']) ??
      this.toRecord(jobRecord?.['runConfiguration']);

    return {
      classIds: this.toStringArray(runConfiguration?.['classIds']),
      studentIds: this.toStringArray(runConfiguration?.['studentIds']),
    };
  }

  private toStringArray(value: unknown): string[] {
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string' && item.length > 0)
      : [];
  }

  private toRecord(value: unknown): Record<string, unknown> | undefined {
    return value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : undefined;
  }

  private toResetAssessmentFailures(value: unknown): ResetAssessmentTestFailure[] {
    return Array.isArray(value) ? value.filter(this.isResetAssessmentFailure) : [];
  }

  private isResetAssessmentFailure(value: unknown): value is ResetAssessmentTestFailure {
    return (
      typeof value === 'string' ||
      typeof value === 'number' ||
      (value != null && typeof value === 'object' && !Array.isArray(value))
    );
  }

  updateScheduledJob(
    jobType: JobTypeEnum | RosteringProvider,
    updates: { hour: string; recurrent: boolean; paused: boolean },
    organization?: string,
  ): Observable<JobSchedule> {
    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.put<JobSchedule>(
          `batch/v1/sources/${organization ?? user.organization}/types/${jobType}/schedule/jobs`,
          updates,
          'scheduledJobs',
        ),
      ),
    );
  }

  startJob(
    jobType: JobTypeEnum | RosteringProvider,
    products?: string[],
    dryRun?: boolean,
    organization?: string,
  ): Observable<Job> {
    return this.withUser().pipe(
      mergeMap((user) => {
        return dryRun
          ? this.apiGatewayService.post<Job>(
              `batch/v1/sources/${organization ?? user.organization}/types/${jobType}/jobs`,
              { configuration: { testMode: true } },
              'configurations',
            )
          : this.apiGatewayService.post<Job>(
              `batch/v1/sources/${organization ?? user.organization}/types/${jobType}/jobs`,
              { runConfiguration: { products } },
            );
      }),
    );
  }

  startUnassignCoursesJob(courses: string[]): Observable<Job> {
    return this.withUser().pipe(
      mergeMap((user) => this.startUnassignCoursesJobFor(user.organization, courses)),
    );
  }

  startUnassignCoursesJobFor(organization: string, courses: string[]): Observable<Job> {
    const assignments = courses
      .map((course) => {
        return course === 'ayla'
          ? aylaCourses.map((aylaCourse) => ({ documentId: aylaCourse }))
          : { documentId: documentId(course) };
      })
      .flat();
    return this.apiGatewayService.post<Job>(
      `batch/v1/sources/${organization}/types/${JobTypeEnum.UNASSIGN_COURSES}/jobs`,
      {
        configuration: { sourceId: organization },
        runConfiguration: { organization, assignments, noEmail: true },
      },
    );
  }

  startResetAssessmentTestsJob(request: {
    benchmark?: string;
    classIds?: string[];
    studentIds?: string[];
    userName?: string;
  }): Observable<Job> {
    if (!request.classIds?.length && !request.studentIds?.length) {
      return throwError(() => new Error('Either classIds or studentIds must be provided.'));
    }

    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.post<Job>(
          `batch/v1/sources/${user.organization}/types/${JobTypeEnum.RESET_ASSESSMENT_TESTS}/jobs`,
          {
            runConfiguration: {
              benchmark: this.getBenchmarkName(request.benchmark),
              email: user.email,
              organization: user.organization,
              ...this.getUserNameConfiguration(request.userName, user.preferredName, user.name),
              ...(request.classIds?.length ? { classIds: request.classIds } : {}),
              ...(request.studentIds?.length ? { studentIds: request.studentIds } : {}),
            },
          },
        ),
      ),
    );
  }

  private getBenchmarkName(benchmark?: string): string {
    const normalizedBenchmark = benchmark?.trim();

    if (!normalizedBenchmark) {
      return 'selected';
    }

    return normalizedBenchmark;
  }

  private getUserNameConfiguration(...names: Array<string | undefined>): { userName?: string } {
    const userName = names.map((name) => name?.trim()).find((name) => !!name);

    return userName ? { userName } : {};
  }

  startBatchUnassignCourses(
    schoolUUID: string,
    assignments: { documentId: string; grades: string[] }[],
  ) {
    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.post<Job>(
          `batch/v1/sources/${user.organization}/types/${JobTypeEnum.UNASSIGN_COURSES}/jobs`,
          {
            configuration: { sourceId: user.organization },
            runConfiguration: {
              organization: user.organization,
              school: schoolUUID,
              user: {
                email: user.email,
                fullName: user.name,
              },
              assignments,
            },
          },
        ),
      ),
    );
  }

  startUpdateSessionTimeJob(
    schoolId: string,
    assignments: CourseAssignmentsPayload[],
  ): Observable<Job> {
    return this.withUser().pipe(
      mergeMap((user) => {
        return this.apiGatewayService.post<Job>(
          `batch/v1/sources/${user.organization}/types/${JobTypeEnum.COURSE_ASSIGNMENT}/jobs`,
          {
            runConfiguration: {
              organization: user.organization,
              school: schoolId,
              user: {
                email: user.email,
                fullName: user.name,
              },
              assignments,
            },
          },
        );
      }),
    );
  }

  getConfigurationForType(
    type: RosteringProvider,
    organization?: string,
  ): Observable<Configuration> {
    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.get<Configuration>(
          `batch/v1/sources/${organization ?? user.organization}/types/${type}/configurations`,
        ),
      ),
      catchError(() => of({} as Configuration)),
    );
  }

  getConfiguration(organization: string): Observable<Configuration> {
    return combineLatest([
      this.getConfigurationForType(RosteringProvider.CLEVER, organization),
      this.getConfigurationForType(RosteringProvider.ONE_ROSTER, organization),
      this.getConfigurationForType(RosteringProvider.ONE_ROSTER_SFTP, organization),
    ]).pipe(
      map(([cleverConfiguration, oneRosterConfiguration, oneRosterConfigurationSFTP]) => {
        if (!isEmpty(cleverConfiguration)) {
          return cleverConfiguration;
        }
        if (!isEmpty(oneRosterConfiguration)) {
          return oneRosterConfiguration;
        }
        if (!isEmpty(oneRosterConfigurationSFTP)) {
          return oneRosterConfigurationSFTP;
        }
        return null;
      }),
    );
  }

  getRosteringProvider(organization?: string): Observable<RosteringProvider> {
    return this.getConfiguration(organization).pipe(
      map(
        (configuration) =>
          // @ts-ignore
          (configuration?.type as RosteringProvider) ?? RosteringProvider.WATERFORD,
      ),
    );
  }

  saveConfiguration(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    configuration: any,
    type: RosteringProvider,
    organization?: string,
  ): Observable<Configuration> {
    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.put<Configuration>(
          `batch/v1/sources/${organization ?? user.organization}/types/${type}/configurations`,
          configuration,
          'configurations',
        ),
      ),
    );
  }

  getJobs(provider: RosteringProvider, organizationId?: string): Observable<Job[]> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.get<Job[]>(
          `batch/v1/sources/${organizationId ?? organization}/types/${provider}/jobs`,
        ),
      ),
    );
  }

  getJobStatus(
    jobId: string,
    provider: RosteringProvider,
    organizationId?: string,
  ): Observable<Job> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.get<Job>(
          `batch/v1/sources/${organizationId ?? organization}/types/${provider}/jobs/${jobId}`,
        ),
      ),
    );
  }

  getJobLogsLocation(organizationId: string, logsUrl: string) {
    return this.apiGatewayService.get(
      `batch/v1/sources/${organizationId}/url/${encodeURIComponent(logsUrl)}`,
    );
  }

  isResetToPlacementScheduled$(organizationId?: string): Observable<boolean> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService
          .get<JobSchedule>(
            `batch/v1/sources/${organizationId ?? organization}/types/${JobTypeEnum.RESET_TO_PLACEMENT}/schedule/jobs`,
          )
          .pipe(
            map((job) => true),
            catchError((error) => {
              if (error.status === 404) {
                return of(false);
              }
              throw error;
            }),
          ),
      ),
    );
  }

  batchRemoveGoals(
    topEntityId: string,
    topEntityType: TopEntityType,
    goals: BatchGoalsRemovalRequest[],
  ) {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.post(
          `batch/v1/sources/${organization}/types/${JobTypeEnum.BATCH_GOALS_REMOVAL}/jobs`,
          {
            configuration: { sourceId: organization },
            runConfiguration: { topEntityId, topEntityType, goals },
          },
        ),
      ),
    );
  }

  startArchiveAssignmentsJob(
    documentIds: string[],
    entityLevels: ArchiveAssignmentEntityLevel[] = [ArchiveAssignmentEntityLevel.ALL],
  ): Observable<Job> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.post<Job>(
          `batch/v1/sources/${organization}/types/${JobTypeEnum.ARCHIVE_ASSIGNMENT}/jobs`,
          {
            configuration: { sourceId: organization },
            runConfiguration: { tenantId: organization, documentIds, entityLevels },
          },
        ),
      ),
    );
  }

  startRolloverProgramYearJob(
    opportunityId: string,
    startDate: string,
    endDate: string,
    schoolYear: string,
  ): Observable<Job> {
    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.post<Job>(
          `batch/v1/sources/${user.organization}/types/${JobTypeEnum.END_OF_YEAR_ROLLOVER}/jobs`,
          {
            configuration: { sourceId: user.organization },
            runConfiguration: {
              organization: user.organization,
              opportunityId: opportunityId,
              startDate: startDate,
              endDate: endDate,
              schoolYear: schoolYear,
              user: {
                email: user.email,
                fullName: user.name,
              },
            },
          },
        ),
      ),
    );
  }
}
